import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft,
  Edit,
  Trash2,
  Copy,
  MoreHorizontal,
  FolderOpen,
  FileText,
  Loader2,
  AlertCircle,
  Search,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  RefreshCw,
  Plus,
  Table2,
  Presentation,
  Mail,
} from 'lucide-react'
import { FluentCard } from '@/components/layout/page-header'
import { PermissionGate } from '@/components/auth/permission-gate'
import { EditHubDialog, CreateHubDialog } from '@/components/hubs'
import { hubService, type TemplateHubMembership } from '@/lib/api/services/hubs'
import { useToast } from '@/components/ui/use-toast'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import type { TemplateHubDetail, TemplateHubTree, UpdateHubRequest } from '@/lib/api/types/hubs'

const SORT_OPTIONS = [
  { label: 'Name (A-Z)', value: 'name-asc' },
  { label: 'Name (Z-A)', value: 'name-desc' },
  { label: 'Templates (Most)', value: 'templateCount-desc' },
  { label: 'Templates (Least)', value: 'templateCount-asc' },
]

const PAGE_SIZE_OPTIONS = [10, 20, 50]

// Helper to get initials from hub name (MS style two-letter acronym)
function getHubInitials(name: string): string {
  const words = name.trim().split(/\s+/)
  if (words.length === 0 || !words[0]) return '??'
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase()
  }
  const first = words[0]?.[0] || ''
  const second = words[1]?.[0] || ''
  return (first + second).toUpperCase()
}

// Helper to get background color from hex color
function getColorStyle(color?: string): React.CSSProperties {
  if (!color) return { backgroundColor: '#3B82F6' } // default blue
  return { backgroundColor: color }
}

export default function HubDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()

  const [hub, setHub] = useState<TemplateHubDetail | null>(null)
  const [allHubs, setAllHubs] = useState<TemplateHubTree[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false)

  // Create child hub dialog state
  const [createChildDialogOpen, setCreateChildDialogOpen] = useState(false)

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // Templates state
  const [templates, setTemplates] = useState<TemplateHubMembership[]>([])
  const [templatesLoading, setTemplatesLoading] = useState(false)

  // Child hubs table state
  const [searchQuery, setSearchQuery] = useState('')
  const [sortValue, setSortValue] = useState('name-asc')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const loadHub = useCallback(async () => {
    if (!id) return

    try {
      setLoading(true)
      setError(null)
      const [hubData, hubTree] = await Promise.all([
        hubService.getHub(id),
        hubService.getHubTree(),
      ])
      setHub(hubData)
      setAllHubs(hubTree)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load hub'
      setError(message)
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }, [id, toast])

  useEffect(() => {
    loadHub()
  }, [loadHub])

  // Load templates when hub is loaded
  const loadTemplates = useCallback(async () => {
    if (!id) return

    try {
      setTemplatesLoading(true)
      const data = await hubService.getHubTemplates(id)
      setTemplates(data)
    } catch (err) {
      console.error('Failed to load templates:', err)
    } finally {
      setTemplatesLoading(false)
    }
  }, [id])

  useEffect(() => {
    if (hub) {
      loadTemplates()
    }
  }, [hub, loadTemplates])

  const handleUpdateHub = async (hubId: string, data: UpdateHubRequest) => {
    try {
      await hubService.updateHub(hubId, data)
      toast({
        title: 'Success',
        description: 'Hub updated successfully',
      })
      loadHub()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update hub'
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      })
      throw err
    }
  }

  const handleDeleteHub = async () => {
    if (!hub) return

    try {
      setDeleting(true)
      await hubService.deleteHub(hub.id)
      toast({
        title: 'Success',
        description: 'Hub deleted successfully',
      })
      navigate('/hubs')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete hub'
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      })
    } finally {
      setDeleting(false)
      setDeleteDialogOpen(false)
    }
  }

  const handleDuplicateHub = async () => {
    if (!hub) return

    try {
      await hubService.duplicateHub(hub.id, `${hub.name} (Copy)`)
      toast({
        title: 'Success',
        description: 'Hub duplicated successfully',
      })
      navigate('/hubs')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to duplicate hub'
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      })
    }
  }

  const handleCreateChildHub = async (data: { name: string; description?: string; icon?: string; color?: string }) => {
    try {
      await hubService.createHub({
        ...data,
        parentId: id, // Set current hub as parent
      })
      toast({
        title: 'Success',
        description: 'Child hub created successfully',
      })
      loadHub()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create child hub'
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      })
      throw err
    }
  }

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  // Filter and sort children
  const filteredChildren = hub?.children
    ? hub.children.filter(child =>
        !searchQuery.trim() ||
        child.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        child.description?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : []

  const [sortField, sortDir] = sortValue.split('-') as [string, 'asc' | 'desc']
  const sortedChildren = [...filteredChildren].sort((a, b) => {
    let comparison = 0
    if (sortField === 'name') {
      comparison = a.name.localeCompare(b.name)
    } else if (sortField === 'templateCount') {
      comparison = a.templateCount - b.templateCount
    }
    return sortDir === 'desc' ? -comparison : comparison
  })

  // Paginate
  const totalCount = sortedChildren.length
  const totalPages = Math.ceil(totalCount / pageSize) || 1
  const startIndex = (page - 1) * pageSize
  const endIndex = startIndex + pageSize
  const paginatedChildren = sortedChildren.slice(startIndex, endIndex)

  // Render page numbers
  const renderPageNumbers = () => {
    const pages: (number | string)[] = []
    const maxVisible = 5

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i)
    } else {
      pages.push(1)
      if (page > 3) pages.push('...')

      const start = Math.max(2, page - 1)
      const end = Math.min(totalPages - 1, page + 1)

      for (let i = start; i <= end; i++) pages.push(i)

      if (page < totalPages - 2) pages.push('...')
      pages.push(totalPages)
    }

    return pages.map((p, idx) =>
      typeof p === 'number' ? (
        <Button
          key={p}
          variant={p === page ? 'default' : 'outline'}
          size="sm"
          onClick={() => setPage(p)}
          className="h-8 w-8 p-0"
        >
          {p}
        </Button>
      ) : (
        <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">...</span>
      )
    )
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        <p className="text-gray-500 dark:text-gray-400">Loading hub...</p>
      </div>
    )
  }

  if (error || !hub) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <div className="p-4 rounded-full bg-red-100 dark:bg-red-900/30">
          <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          {error || 'Hub not found'}
        </h2>
        <Button variant="outline" onClick={() => navigate('/hubs')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Hubs
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Back Link */}
      <Link
        to="/hubs"
        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Hubs
      </Link>

      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-4">
          {/* Hub Icon with Initials */}
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center text-white font-bold text-xl shadow-lg shrink-0"
            style={getColorStyle(hub.color)}
          >
            {getHubInitials(hub.name)}
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {hub.name}
              </h1>
              <Badge
                variant={hub.isActive ? 'default' : 'secondary'}
                className={hub.isActive ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : ''}
              >
                {hub.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </div>
            {hub.description && (
              <p className="text-gray-500 dark:text-gray-400 mt-1 max-w-xl">
                {hub.description}
              </p>
            )}
            {/* Breadcrumbs */}
            {hub.breadcrumbs && hub.breadcrumbs.length > 0 && (
              <div className="flex items-center gap-1 mt-2 text-sm text-gray-500 dark:text-gray-400">
                {hub.breadcrumbs.map((crumb, idx) => (
                  <span key={crumb.id} className="flex items-center gap-1">
                    {idx > 0 && <span>/</span>}
                    <Link
                      to={`/hubs/${crumb.id}`}
                      className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      {crumb.name}
                    </Link>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={loadHub} disabled={loading}>
            <RefreshCw className={cn('h-4 w-4 mr-2', loading && 'animate-spin')} />
            Refresh
          </Button>
          <PermissionGate permissions={['templates.create']}>
            <Button onClick={() => setCreateChildDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Child Hub
            </Button>
          </PermissionGate>
          <PermissionGate permissions={['templates.update']}>
            <Button variant="outline" onClick={() => setEditDialogOpen(true)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </PermissionGate>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={handleDuplicateHub}>
                <Copy className="h-4 w-4 mr-2" />
                Duplicate Hub
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setDeleteDialogOpen(true)}
                className="text-red-600 dark:text-red-400 focus:bg-red-50 dark:focus:bg-red-900/20"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Hub
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        <FluentCard className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600">
            <FileText className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{hub.templateCount}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Templates</p>
          </div>
        </FluentCard>
        <FluentCard className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-violet-400 to-purple-600">
            <FolderOpen className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{hub.children?.length || 0}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Child Hubs</p>
          </div>
        </FluentCard>
        <FluentCard className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-green-400 to-green-600">
            <FolderOpen className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">{formatDate(hub.createdAt)}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Created</p>
          </div>
        </FluentCard>
      </div>

      {/* Child Hubs Section */}
      {hub.children && hub.children.length > 0 && (
        <FluentCard>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Child Hubs ({hub.children.length})
            </h2>
            <div className="flex items-center gap-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value)
                    setPage(1)
                  }}
                  className="pl-10 w-[200px]"
                />
              </div>
              {/* Sort */}
              <Select value={sortValue} onValueChange={(value) => { setSortValue(value); setPage(1) }}>
                <SelectTrigger className="w-[160px]">
                  <ArrowUpDown className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Child Hubs Table */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 dark:bg-gray-800/50">
                  <TableHead className="w-[300px]">Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-[120px] text-center">Templates</TableHead>
                  <TableHead className="w-[100px]">Status</TableHead>
                  <TableHead className="w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedChildren.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                      {searchQuery ? `No child hubs matching "${searchQuery}"` : 'No child hubs'}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedChildren.map((child) => (
                    <TableRow
                      key={child.id}
                      className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                      onClick={() => navigate(`/hubs/${child.id}`)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-semibold text-sm shrink-0"
                            style={getColorStyle(child.color)}
                          >
                            {getHubInitials(child.name)}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 dark:text-white truncate">
                              {child.name}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-[300px]">
                          {child.description || '—'}
                        </p>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary" className="font-mono">
                          {child.templateCount}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={child.isActive ? 'default' : 'secondary'}
                          className={child.isActive ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : ''}
                        >
                          {child.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/hubs/${child.id}`)
                          }}
                          className="h-8 w-8"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalCount > pageSize && (
            <div className="flex items-center justify-between mt-4 px-2">
              <div className="flex items-center gap-2">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Showing {startIndex + 1} - {Math.min(endIndex, totalCount)} of {totalCount}
                </p>
                <Select value={pageSize.toString()} onValueChange={(value) => { setPageSize(Number(value)); setPage(1) }}>
                  <SelectTrigger className="w-[80px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={size.toString()}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="h-8"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                {renderPageNumbers()}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages}
                  className="h-8"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </FluentCard>
      )}

      {/* Empty state for no children */}
      {(!hub.children || hub.children.length === 0) && (
        <FluentCard>
          <div className="flex flex-col items-center justify-center py-12">
            <FolderOpen className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-4" />
            <p className="text-gray-500 dark:text-gray-400 mb-2">No child hubs</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mb-4">
              Create a child hub to organize templates within this hub
            </p>
            <Button variant="outline" onClick={() => setCreateChildDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Child Hub
            </Button>
          </div>
        </FluentCard>
      )}

      {/* Templates Section */}
      <FluentCard>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Templates ({templates.length})
          </h2>
          <Button variant="outline" onClick={() => navigate('/templates/new')}>
            <Plus className="h-4 w-4 mr-2" />
            Add Template
          </Button>
        </div>

        {templatesLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400 mb-2" />
            <p className="text-gray-500 dark:text-gray-400">Loading templates...</p>
          </div>
        ) : templates.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-4" />
            <p className="text-gray-500 dark:text-gray-400 mb-2">No templates in this hub</p>
            <p className="text-sm text-gray-400 dark:text-gray-500">
              Add templates to this hub from the Templates page
            </p>
          </div>
        ) : (
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 dark:bg-gray-800/50">
                  <TableHead className="w-[60px]">Type</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead className="w-[100px]">Status</TableHead>
                  <TableHead className="w-[150px]">Added</TableHead>
                  <TableHead className="w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((template) => (
                  <TableRow
                    key={template.templateId}
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                    onClick={() => navigate(`/templates/${template.templateId}`)}
                  >
                    <TableCell>
                      <div className="flex items-center justify-center">
                        {template.templateType === 'Word' && <FileText className="h-5 w-5 text-blue-600" />}
                        {template.templateType === 'Excel' && <Table2 className="h-5 w-5 text-green-600" />}
                        {template.templateType === 'PowerPoint' && <Presentation className="h-5 w-5 text-orange-600" />}
                        {template.templateType === 'Outlook' && <Mail className="h-5 w-5 text-purple-600" />}
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="font-medium text-gray-900 dark:text-white">{template.templateName}</p>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={template.isActive ? 'default' : 'secondary'}
                        className={template.isActive ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : ''}
                      >
                        {template.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(template.addedAt)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/templates/${template.templateId}`)
                        }}
                        className="h-8 w-8"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </FluentCard>

      {/* Hub Details Card */}
      <FluentCard>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Hub Details
        </h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Created</dt>
            <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatDate(hub.createdAt)}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Updated</dt>
            <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatDate(hub.updatedAt)}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Parent Hub</dt>
            <dd className="mt-1 text-sm text-gray-900 dark:text-white">
              {hub.parent ? (
                <Link
                  to={`/hubs/${hub.parent.id}`}
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {hub.parent.name}
                </Link>
              ) : (
                '—'
              )}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Color</dt>
            <dd className="mt-1 flex items-center gap-2">
              <div
                className="w-6 h-6 rounded-md border border-gray-200 dark:border-gray-700"
                style={getColorStyle(hub.color)}
              />
              <span className="text-sm text-gray-900 dark:text-white font-mono">
                {hub.color || 'Default'}
              </span>
            </dd>
          </div>
        </dl>
      </FluentCard>

      {/* Edit Dialog */}
      <EditHubDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        hub={hub}
        onSubmit={handleUpdateHub}
        parentHubs={allHubs}
      />

      {/* Create Child Hub Dialog */}
      <CreateHubDialog
        open={createChildDialogOpen}
        onOpenChange={setCreateChildDialogOpen}
        onSubmit={handleCreateChildHub}
        initialParentId={hub.id}
        parentHubs={allHubs}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Hub</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{hub.name}"?
              {hub.children && hub.children.length > 0 && (
                <span className="block mt-2 text-orange-600 dark:text-orange-400">
                  Warning: This hub has {hub.children.length} child hub(s) that will also be deleted.
                </span>
              )}
              {hub.templateCount > 0 && (
                <span className="block mt-2 text-orange-600 dark:text-orange-400">
                  Warning: {hub.templateCount} template(s) are assigned to this hub.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteHub}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
